<template>
  <keep-alive>
    <b-modal
        ref="modal"
        v-bind="$attrs"
        :id="id"
        :title="modalTitle"
        @ok="handleOk"
        title-class="font-18"
        no-close-on-backdrop
    >
      <validation-observer ref="form_observer" v-if="!loading">
        <component
            :is="form"
            v-bind="formProps"
            v-on="formListeners"
            v-model="model"
            :is-valid.sync="isValid"
            :errors="errors"
            ref="form"
            is-modal="true"
        >
        </component>
      </validation-observer>
      <div v-if="loading" class="d-flex">
        <i class="bx bx-loader bx-spin font-size-16 align-middle mx-auto"></i>
      </div>
      <template #modal-footer="{ ok, cancel}">
        <b-button size="md" variant="outline-dark" @click="cancel()">
          Batal
        </b-button>
        <b-button
            size="md"
            variant="primary"
            @click="ok()"
            :disabled="updating || loading"
        >
          <i
              v-if="updating"
              class="bx bx-loader bx-spin font-size-16 align-middle me-2"
          ></i>
          Simpan
        </b-button>
      </template>
    </b-modal>
  </keep-alive>
</template>

<script>
import ModelEditModal from "./modal-edit";
import FormsModel from "@/components/forms/customer-edit.vue";
import CustomerFarm from "@/components/forms/customer/customer-farm";
import MultiselectAjax from "@/components/inputs/vue-multiselect-ajax";
import { buildCustomerPayload } from "@/services/transformers/customerPayload";

export default {
  extends: ModelEditModal,
  components: {
    CustomerFarm,
    MultiselectAjax,
  },
  props: {
    id: { type: String, required: true },
    modalTitle: {
      default() {
        return this.lodash.startCase(this.$t("customers.singular"));
      },
    },
    resourceRoute: { default: "models" },
    modelVariable: { default: "model" },
    form: {
      type: Object,
      default: function() {
        return FormsModel;
      },
    },
    formProps: {
      type: Object,
      default: function() {
        return {};
      },
    },
    formListeners: {
      type: Object,
      default: function() {
        return {};
      },
    },
    initialValue: {
      type: Object,
      default() {
        return {};
      },
    },
    denyCustomerSelect: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      loading: false,
      isValid: true,
      updating: false,
      model: {
        ...this.initialValue,
        farms: [],
      },
      errors: {},
      registered:
          this.initialValue?.registered !== undefined
              ? this.initialValue.registered
              : true,
    };
  },
  methods: {
    fetchFarm(farm) {
      return this.$axios
          .get(
              farm.use_jala_farm ?
                  `/jala/farms/${farm.jala_farm_id}` :
                  `/farms/${farm.id}`
          )
          .then((response) => {
            const {
              data: { data },
            } = response;
            return {
              ...data,
              ...farm,
              region_id: farm.region_id ? farm.region_id.toString() : null,
              get coordinate() {
                return this.latitude
                    ? `${this.latitude},${this.longitude}`
                    : null;
              },
              set coordinate(value) {
                if (value) {
                  const [lat, lng] = (value || "").split(",");
                  if (lat != null && Number(lat) && lng != null && Number(lng)) {
                    this.latitude = Number(lat);
                    this.longitude = Number(lng);
                  }
                } else {
                  this.latitude = null;
                  this.longitude = null;
                }
              },
            };
          })
          .catch((error) => this.failedAlert(error));
    },
    async fetchModel(customer_id = null) {
      try {
        this.loading = true;
        const response = await this.$axios.get(`/customers/${customer_id}`, {
          params: {
            with: "bank_accounts.bank,farms,addresses,finance_account",
          },
        });
        const {
          data: { data },
        } = response;
        const farmResponses = await Promise.all(
            data.farms.map((farm) => this.fetchFarm(farm))
        );
        const addresses = {};
        ["partner", "processing"].map((customer_type) => {
          const address =
              data.addresses.find((item) => item.type == customer_type) || {};
          addresses[customer_type] = {
            latitude: null,
            longitude: null,
            total_person: null,
            ...address,
            get coordinate() {
              return address.latitude
                  ? `${address.latitude},${address.longitude}`
                  : null;
            },
            set coordinate(value) {
              if (value) {
                const [lat, lng] = (value || "").split(",");
                if (lat != null && Number(lat) && lng != null && Number(lng)) {
                  address.latitude = Number(lat);
                  address.longitude = Number(lng);
                }
              } else {
                address.latitude = null;
                address.longitude = null;
              }
            },
          };
        });
        this.model = {
          ...data,
          region_id: data.region_id ? data.region_id.toString() : null,
          farms: farmResponses,
          ...addresses,
        };
        return response;
      } catch (error) {
        this.failedAlert(error);
      } finally {
        this.loading = false;
      }
    },
    handleOk(event) {
      event.preventDefault();
      if (!('validateAll' in this.$refs.form)) return;
      this.$refs.form.validateAll().then((isValids) => {
        this.isValid = isValids.every((isValid) => isValid === true);
        if (this.isValid) {
          this.$nextTick(() => {
            this.handleUpdate(this.model)
          })
        }
      }).finally(() => {
        this.$set(this, 'model', {
          ...this.initialValue,
          farms: [],
        })
      })
    },
    handleUpdate(value) {
      this.updating = true;
      this.update(value)
          .then(() => this.updateSuccessAlert(this.getModelName()))
          .then(() => {
            this.isEditing = false;
            this.$emit('updated', value);
            this.$refs.modal.hide();
          })
          .catch((error) => this.failedAlert(error))
          .finally(() => { this.updating = false; });
    },
    update(value) {
      const payload = buildCustomerPayload(value);
      return this.$axios.put(`/customers/${value.id}`, payload);
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .asterisk-after-text {
    .col-form-label::after {
      content: " *";
      color: #DC2626;
    }
  }
  .custom-checkbox .custom-control-label::before {
    border-radius: 0.25rem;
  }

  .col-form-label {
    font: 800 14px/20px Lato, 'sans-serif' !important;
    color: #182230 !important;
  }

  .btn {
    font-weight: 600;

    &.btn-white {
      &.type-button-white {
        border: 1px solid #D0D5DD;
        color: #182230;
      }

      &.disabled {
        color: #98A2B3;
        background-color: #EAECF0;
        border: none;

      }
    }
  }

  .modal-title {
    font-weight: 800;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0.05em;
    color: #0084f3;
  }

  .modal-dialog {
    max-width: 720px !important;
  }

  .col-form-label {
    font-weight: 400;
    font-size: 14px;
    line-height: 16px;
    color: #6c7589;
  }
}
</style>
